import type { AIUsageInfo } from '@/types';
import type { CodeGenerationChunk, StreamingCallback } from '@/types';

// Error class that preserves usage and rawResponse when AI call parsing fails
export class AIResponseParseError extends Error {
  usage?: AIUsageInfo;
  /**
   * Adapter-extracted content used by Midscene for parsing. This is not the
   * full provider response or choices[0].message.
   */
  rawResponse: string;
  rawChoiceMessage?: unknown;
  reasoningContent?: string;

  constructor(
    message: string,
    rawResponse: string,
    usage?: AIUsageInfo,
    rawChoiceMessage?: unknown,
    reasoningContent?: string,
  ) {
    super(message);
    this.name = 'AIResponseParseError';
    this.rawResponse = rawResponse;
    this.usage = usage;
    this.rawChoiceMessage = rawChoiceMessage;
    this.reasoningContent = reasoningContent;
  }
}
import {
  type IModelConfig,
  MIDSCENE_LANGFUSE_DEBUG,
  MIDSCENE_LANGSMITH_DEBUG,
  type TModelFamily,
  globalConfigManager,
} from '@midscene/shared/env';

import { getDebug } from '@midscene/shared/logger';
import { assert, ifInBrowser, uuid } from '@midscene/shared/utils';
import OpenAI from 'openai';
import type { ChatCompletionMessageParam } from 'openai/resources/index';
import type { Stream } from 'openai/streaming';
import { getVersion } from '../../utils';
import type { ModelRuntime } from '../models';
import { type JsonParserSource, assertJsonObject } from '../shared/json';
import {
  callAIWithCodexAppServer,
  isCodexAppServerProvider,
} from './codex-app-server';
import type { CodexAppServerRecordEvent } from './codex-app-server';
import {
  isModelCallRecordingEnabled,
  recordModelCallEvent,
} from './model-call-recorder';
import {
  type OpenAIErrorResponseContext,
  formatOpenAIAPIErrorDetails,
  wrapOpenAICompatibleFetch,
} from './openai-error';
import {
  buildRequestAbortSignal,
  isHardTimeoutError,
  resolveEffectiveTimeoutMs,
  restoreHardTimeoutError,
} from './request-timeout';
import {
  callAiAndParseWithRetry,
  withSemanticRetryFeedback,
} from './semantic-retry';
import type { AIArgs } from './types';
export {
  extractJSONFromCodeBlock,
  parseModelResponseJson,
} from '../shared/json';
export type { JsonParser } from '../shared/json';

/**
 * Internal field name stamped onto every AIUsageInfo shaped by callAI().
 * Used for cross-path dedup when the provider does not return a request_id.
 */
export const INTERNAL_CALL_ID_FIELD = '_midscene_call_id';

let internalCallIdCounter = 0;
function nextInternalCallId(): string {
  internalCallIdCounter += 1;
  return `call_${internalCallIdCounter}`;
}

function stringifyForDebug(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch (_error) {
    return String(value);
  }
}

function getLatestSuccessfulResponseRequestId(
  context: OpenAIErrorResponseContext,
): string | undefined {
  return context.responseRequestIds?.reduce<string | undefined>(
    (latestRequestId, response) =>
      response.ok ? response.requestId : latestRequestId,
    undefined,
  );
}

function getLatestResponseAttempt(context: OpenAIErrorResponseContext) {
  return context.httpResponses?.at(-1)?.attempt ?? 1;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function normalizeRetryCount(retryCount: unknown): number {
  if (typeof retryCount !== 'number' || !Number.isFinite(retryCount)) {
    return 1;
  }

  return Math.max(0, Math.floor(retryCount));
}

function appendAIRequestFailureSummary<T extends Error>(
  error: T,
  attemptErrors: Array<{ attempt: number; error: unknown }>,
  maxAttempts: number,
): T {
  const failedAttempts = attemptErrors.length;
  const retries = Math.max(0, failedAttempts - 1);
  const retryLabel = retries === 1 ? 'retry' : 'retries';
  const originalMessage = error.message;
  const previousAttemptErrors = attemptErrors.slice(0, -1);

  error.message = `AI model request failed after ${retries} ${retryLabel} (${failedAttempts}/${maxAttempts} attempts). Last error: ${originalMessage}`;

  if (previousAttemptErrors.length === 0) {
    return error;
  }

  const details = previousAttemptErrors
    .map(
      ({ attempt, error }) => `Attempt ${attempt}: ${getErrorMessage(error)}`,
    )
    .join('\n');

  error.message = `${error.message}\nPrevious AI call attempt errors:\n${details}`;
  return error;
}

export async function createChatClient({
  modelConfig,
  executionId,
  recordEvent,
}: {
  modelConfig: IModelConfig;
  executionId: string;
  recordEvent?: (event: Record<string, unknown>) => void;
}): Promise<{
  completion: OpenAI.Chat.Completions;
  modelName: string;
  modelDescription: string;
  modelFamily: TModelFamily | undefined;
  openAIErrorResponseContext: OpenAIErrorResponseContext;
}> {
  const {
    socksProxy,
    httpProxy,
    modelName,
    openaiBaseURL,
    openaiApiKey,
    openaiExtraConfig,
    modelDescription,
    modelFamily,
    createOpenAIClient,
    timeout,
  } = modelConfig;

  let proxyAgent: any = undefined;
  const warnClient = getDebug('ai:call', { console: true });
  const debugProxy = getDebug('ai:call:proxy');
  const warnProxy = getDebug('ai:call:proxy', { console: true });

  // Helper function to sanitize proxy URL for logging (remove credentials)
  // Uses URL API instead of regex to avoid ReDoS vulnerabilities
  const sanitizeProxyUrl = (url: string): string => {
    try {
      const parsed = new URL(url);
      if (parsed.username) {
        // Keep username for debugging, hide password for security
        parsed.password = '****';
        return parsed.href;
      }
      return url;
    } catch {
      // If URL parsing fails, return original URL (will be caught later)
      return url;
    }
  };

  if (httpProxy) {
    debugProxy('using http proxy', sanitizeProxyUrl(httpProxy));
    if (ifInBrowser) {
      warnProxy(
        'HTTP proxy is configured but not supported in browser environment',
      );
    } else {
      const { loadUndici } = await import('#proxy-deps');
      const { ProxyAgent } = await loadUndici();
      proxyAgent = new ProxyAgent({
        uri: httpProxy,
        // Note: authentication is handled via the URI (e.g., http://user:pass@proxy.com:8080)
      });
    }
  } else if (socksProxy) {
    debugProxy('using socks proxy', sanitizeProxyUrl(socksProxy));
    if (ifInBrowser) {
      warnProxy(
        'SOCKS proxy is configured but not supported in browser environment',
      );
    } else {
      try {
        const { loadFetchSocks } = await import('#proxy-deps');
        const { socksDispatcher } = await loadFetchSocks();
        // Parse SOCKS proxy URL (e.g., socks5://127.0.0.1:1080)
        const proxyUrl = new URL(socksProxy);

        // Validate hostname
        if (!proxyUrl.hostname) {
          throw new Error('SOCKS proxy URL must include a valid hostname');
        }

        // Validate and parse port
        const port = Number.parseInt(proxyUrl.port, 10);
        if (!proxyUrl.port || Number.isNaN(port)) {
          throw new Error('SOCKS proxy URL must include a valid port');
        }

        // Parse SOCKS version from protocol
        const protocol = proxyUrl.protocol.replace(':', '');
        const socksType =
          protocol === 'socks4' ? 4 : protocol === 'socks5' ? 5 : 5;

        proxyAgent = socksDispatcher({
          type: socksType,
          host: proxyUrl.hostname,
          port,
          ...(proxyUrl.username
            ? {
                userId: decodeURIComponent(proxyUrl.username),
                password: decodeURIComponent(proxyUrl.password || ''),
              }
            : {}),
        });
        debugProxy('socks proxy configured successfully', {
          type: socksType,
          host: proxyUrl.hostname,
          port: port,
        });
      } catch (error) {
        warnProxy('Failed to configure SOCKS proxy:', error);
        throw new Error(
          `Invalid SOCKS proxy URL: ${socksProxy}. Expected format: socks4://host:port, socks5://host:port, or with authentication: socks5://user:pass@host:port`,
        );
      }
    }
  }

  const effectiveTimeoutMs = resolveEffectiveTimeoutMs({ timeout });
  const openAIErrorResponseContext: OpenAIErrorResponseContext = {
    recordEvent,
  };
  const openAIOptions = {
    baseURL: openaiBaseURL,
    apiKey: openaiApiKey,
    // Use fetchOptions.dispatcher for fetch-based SDK instead of httpAgent
    // Note: Type assertion needed due to undici version mismatch between dependencies
    ...(proxyAgent ? { fetchOptions: { dispatcher: proxyAgent as any } } : {}),
    ...openaiExtraConfig,
    // Midscene exposes this setting through MIDSCENE_*_INIT_CONFIG_JSON, so
    // defaultHeaders is expected to be a plain JSON object rather than another
    // HeadersLike representation supported by the OpenAI SDK.
    defaultHeaders: {
      ...(openaiExtraConfig?.defaultHeaders as
        | Record<string, string>
        | undefined),
      // These Midscene-owned headers intentionally override user-supplied
      // headers with the same names.
      'x-midscene-version': getVersion(),
      'x-midscene-execution-id': executionId,
    },
    fetch: wrapOpenAICompatibleFetch(openAIErrorResponseContext),
    // Midscene already handles retries in callAI(), so disable SDK-level retries
    // to avoid duplicate attempts and duplicated backoff latency.
    maxRetries: 0,
    // When disabled (timeoutMs === null) fall through to the SDK default so
    // only the caller-provided abortSignal can cancel the request.
    ...(effectiveTimeoutMs !== null ? { timeout: effectiveTimeoutMs } : {}),
    dangerouslyAllowBrowser: true,
  };

  const baseOpenAI = new OpenAI(openAIOptions);

  let openai: OpenAI = baseOpenAI;

  // LangSmith wrapper
  if (
    openai &&
    globalConfigManager.getEnvConfigInBoolean(MIDSCENE_LANGSMITH_DEBUG)
  ) {
    if (ifInBrowser) {
      throw new Error('langsmith is not supported in browser');
    }
    warnClient('DEBUGGING MODE: langsmith wrapper enabled');
    // Use variable to prevent static analysis by bundlers
    const langsmithModule = 'langsmith/wrappers';
    const { wrapOpenAI } = await import(langsmithModule);
    openai = wrapOpenAI(openai);
  }

  // Langfuse wrapper
  if (
    openai &&
    globalConfigManager.getEnvConfigInBoolean(MIDSCENE_LANGFUSE_DEBUG)
  ) {
    if (ifInBrowser) {
      throw new Error('langfuse is not supported in browser');
    }
    warnClient('DEBUGGING MODE: langfuse wrapper enabled');
    // Use variable to prevent static analysis by bundlers
    const langfuseModule = '@langfuse/openai';
    const { observeOpenAI } = await import(langfuseModule);
    openai = observeOpenAI(openai);
  }

  if (createOpenAIClient) {
    const wrappedClient = await createOpenAIClient(baseOpenAI, openAIOptions);

    if (wrappedClient) {
      openai = wrappedClient as OpenAI;
    }
  }

  return {
    completion: openai.chat.completions,
    modelName,
    modelDescription,
    modelFamily,
    openAIErrorResponseContext,
  };
}

interface CallAIOptions {
  stream?: boolean;
  onChunk?: StreamingCallback;
  abortSignal?: AbortSignal;
  requiresOriginalImageDetail?: boolean;
  expectedJsonObjectResponse?: boolean;
  /**
   * Number of preceding semantic parsing failures for this request.
   * Network retries are intentionally excluded.
   */
  semanticRetryAttempt?: number;
}

export async function callAI(
  messages: ChatCompletionMessageParam[],
  modelRuntime: ModelRuntime,
  options?: CallAIOptions,
): Promise<{
  content: string;
  reasoning_content?: string;
  rawChoiceMessage?: unknown;
  usage?: AIUsageInfo;
  isStreamed: boolean;
}> {
  const { config: modelConfig, adapter } = modelRuntime;
  // Low-level callers without a TaskRunner still need a stable ID for the
  // lifetime of this model call (including its network retries).
  const executionId = modelRuntime.executionId ?? `unscoped-${uuid()}`;

  // Stable internal ID for this call, used by the agent to deduplicate usage
  // across the onUsage callback and the task-dump-based collectUsageMetrics()
  // path when the provider does not return a request_id.
  const internalCallId = nextInternalCallId();
  const recordEvent = isModelCallRecordingEnabled()
    ? (event: Record<string, unknown>) => {
        void recordModelCallEvent({
          executionId,
          callId: internalCallId,
          semanticRetryAttempt: options?.semanticRetryAttempt,
          slot: modelConfig.slot,
          intent: modelConfig.intent,
          modelFamily: modelConfig.modelFamily,
          ...event,
        });
      }
    : undefined;
  const chatCompletionInput = {
    intent: modelConfig.intent,
    userConfig: {
      temperature: modelConfig.temperature,
      reasoningEnabled: modelConfig.reasoningEnabled,
      reasoningEffort: modelConfig.reasoningEffort,
      reasoningBudget: modelConfig.reasoningBudget,
      responseFormat: modelConfig.responseFormat,
    },
    semanticRetryAttempt: options?.semanticRetryAttempt,
    requiresOriginalImageDetail: options?.requiresOriginalImageDetail,
    expectedJsonObjectResponse: options?.expectedJsonObjectResponse,
  };
  const imageDetail =
    adapter.chatCompletion.resolveImageDetail(chatCompletionInput);

  if (isCodexAppServerProvider(modelConfig.openaiBaseURL)) {
    let protocolChunkSequence = 0;
    const codexStartTime = Date.now();
    const recordCodexEvent = recordEvent
      ? (event: CodexAppServerRecordEvent) => {
          if (event.type === 'chunk') {
            protocolChunkSequence += 1;
            recordEvent({
              ...event,
              attempt: 1,
              sequence: protocolChunkSequence,
              provider: 'codex-app-server',
            });
            return;
          }

          recordEvent({
            ...event,
            attempt: 1,
            provider: 'codex-app-server',
          });
        }
      : undefined;

    try {
      const codexResult = await callAIWithCodexAppServer(
        messages,
        modelConfig,
        {
          stream: options?.stream,
          onChunk: options?.onChunk,
          reasoningEnabled: modelConfig.reasoningEnabled,
          abortSignal: options?.abortSignal,
          imageDetail,
          onRecordEvent: recordCodexEvent,
        },
      );
      const { protocolMetadata, ...response } = codexResult;
      recordEvent?.({
        type: 'response',
        attempt: 1,
        provider: 'codex-app-server',
        final: {
          content: response.content,
          reasoningContent: response.reasoning_content,
          usage: response.usage,
          timeCost: Date.now() - codexStartTime,
          protocol: protocolMetadata,
        },
      });
      if (response.usage) {
        (response.usage as any)[INTERNAL_CALL_ID_FIELD] = internalCallId;
        if (modelRuntime.onUsage) {
          modelRuntime.onUsage(response.usage);
        }
      }
      return {
        ...response,
      };
    } catch (error) {
      recordEvent?.({
        type: 'error',
        attempt: 1,
        provider: 'codex-app-server',
        error:
          error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack,
              }
            : String(error),
      });
      throw error;
    }
  }

  const {
    completion,
    modelName,
    modelDescription,
    modelFamily,
    openAIErrorResponseContext,
  } = await createChatClient({
    modelConfig,
    executionId,
    recordEvent,
  });
  const effectiveTimeoutMs = resolveEffectiveTimeoutMs(modelConfig);

  const extraBody = modelConfig.extraBody;

  const debugCall = getDebug('ai:call');
  const warnCall = getDebug('ai:call', { console: true });
  const debugProfileStats = getDebug('ai:profile:stats');
  const debugProfileDetail = getDebug('ai:profile:detail');

  const startTime = Date.now();

  const isStreaming = options?.stream && options?.onChunk;
  const { config: adapterChatCompletionParams } =
    adapter.chatCompletion.buildChatCompletionParams(chatCompletionInput);
  debugCall(
    `adapter chat completion params: ${stringifyForDebug({
      config: adapterChatCompletionParams,
    })}`,
  );
  let content: string | undefined;
  let accumulated = '';
  let accumulatedReasoning = '';
  let rawChoiceMessage: unknown;
  let usage: OpenAI.CompletionUsage | undefined;
  let timeCost: number | undefined;
  let requestId: string | null | undefined;
  let responseModelName: string | undefined;
  // Tracks whether onUsage has already been fired for this call (e.g. from
  // the streaming final-chunk handler), so the final return does not double-fire.
  let usageReported = false;

  const hasUsableText = (value: string | null | undefined): value is string =>
    typeof value === 'string' && value.trim().length > 0;

  const resolveContentWithReasoningFallback = (
    contentValue: string | undefined,
    reasoningContent: string,
  ) => {
    if (
      !hasUsableText(contentValue) &&
      adapter.chatCompletion.useReasoningAsContentFallback &&
      hasUsableText(reasoningContent)
    ) {
      warnCall('empty content from AI model, using reasoning content');
      return reasoningContent;
    }

    return contentValue;
  };

  const buildUsageInfo = (
    usageData?: OpenAI.CompletionUsage,
    requestId?: string | null,
  ) => {
    if (!usageData) return undefined;

    const cachedInputTokens = (
      usageData as { prompt_tokens_details?: { cached_tokens?: number } }
    )?.prompt_tokens_details?.cached_tokens;

    return {
      ...usageData,
      prompt_tokens: usageData.prompt_tokens ?? 0,
      completion_tokens: usageData.completion_tokens ?? 0,
      total_tokens: usageData.total_tokens ?? 0,
      cached_input: cachedInputTokens ?? 0,
      time_cost: timeCost ?? 0,
      model_name: modelName,
      model_description: modelDescription,
      response_model_name: responseModelName,
      slot: modelConfig.slot,
      // Left undefined at the raw call layer. The agent's onUsage callback
      // fills it from modelConfig.slot for metrics collection, and task
      // layers use withUsageIntent() to stamp a more specific semantic
      // intent (e.g. 'planning', 'insight') when attaching usage to tasks.
      intent: undefined,
      request_id: requestId ?? undefined,
      // Internal stable ID for cross-path dedup when request_id is absent.
      [INTERNAL_CALL_ID_FIELD]: internalCallId,
    } satisfies AIUsageInfo;
  };

  const requestConfig = {
    ...adapterChatCompletionParams,
    ...(extraBody ?? {}),
  };
  const temperature = requestConfig.temperature;

  // Some adapters request original image detail to preserve screenshot
  // resolution for localization-sensitive tasks.
  const messagesWithImageDetail: ChatCompletionMessageParam[] = (() => {
    if (!imageDetail) {
      return messages;
    }

    return messages.map((msg) => {
      if (!Array.isArray(msg.content)) {
        return msg;
      }

      const content = msg.content.map((part) => {
        if (part && part.type === 'image_url' && part.image_url?.url) {
          return {
            ...part,
            image_url: {
              ...part.image_url,
              detail: imageDetail,
            },
          };
        }
        return part;
      });

      return {
        ...msg,
        content,
      } as ChatCompletionMessageParam;
    });
  })();

  try {
    debugCall(
      `sending ${isStreaming ? 'streaming ' : ''}request to ${modelName}`,
    );

    if (isStreaming) {
      const { signal: streamSignal, cleanup: cleanupStreamSignal } =
        buildRequestAbortSignal(effectiveTimeoutMs, options?.abortSignal);
      try {
        const stream = (await completion.create(
          {
            model: modelName,
            messages: messagesWithImageDetail,
            ...requestConfig,
            stream: true,
          },
          {
            stream: true,
            signal: streamSignal,
          },
        )) as Stream<OpenAI.Chat.Completions.ChatCompletionChunk> & {
          _request_id?: string | null;
        };

        requestId =
          getLatestSuccessfulResponseRequestId(openAIErrorResponseContext) ??
          stream._request_id;
        const streamAttempt = getLatestResponseAttempt(
          openAIErrorResponseContext,
        );

        let chunkSequence = 0;
        for await (const chunk of stream) {
          chunkSequence += 1;
          recordEvent?.({
            type: 'chunk',
            attempt: streamAttempt,
            sequence: chunkSequence,
            chunk,
          });
          const parsedChunk = adapter.chatCompletion.extractContentAndReasoning(
            chunk.choices?.[0]?.delta,
          );
          const content = parsedChunk.content || '';
          const reasoning_content = parsedChunk.reasoning_content || '';

          // Check for usage info in any chunk (OpenAI provides usage in separate chunks)
          if (chunk.usage) {
            usage = chunk.usage;
          }
          if (chunk.model) {
            responseModelName = chunk.model;
          }

          if (content || reasoning_content) {
            accumulated += content;
            accumulatedReasoning += reasoning_content;
            const chunkData: CodeGenerationChunk = {
              content,
              reasoning_content,
              accumulated,
              isComplete: false,
              usage: undefined,
            };
            options.onChunk!(chunkData);
          }

          // Check if stream is complete
          if (chunk.choices?.[0]?.finish_reason) {
            timeCost = Date.now() - startTime;

            // If usage is not available from the stream, provide a basic usage info
            if (!usage) {
              // Estimate token counts based on content length (rough approximation)
              const estimatedTokens = Math.max(
                1,
                Math.floor(accumulated.length / 4),
              );
              usage = {
                prompt_tokens: estimatedTokens,
                completion_tokens: estimatedTokens,
                total_tokens: estimatedTokens * 2,
              };
            }

            const finalAccumulated = resolveContentWithReasoningFallback(
              accumulated,
              accumulatedReasoning,
            );
            accumulated = finalAccumulated || '';

            // Send final chunk
            const finalUsage = buildUsageInfo(usage, requestId);
            if (finalUsage && modelRuntime.onUsage) {
              modelRuntime.onUsage(finalUsage);
              usageReported = true;
            }
            const finalChunk: CodeGenerationChunk = {
              content: '',
              accumulated,
              reasoning_content: '',
              isComplete: true,
              usage: finalUsage,
            };
            options.onChunk!(finalChunk);
            break;
          }
        }
      } catch (error) {
        throw restoreHardTimeoutError(toError(error), streamSignal);
      } finally {
        cleanupStreamSignal();
      }
      content = accumulated;
      debugProfileStats(
        `streaming model, ${modelName}, mode, ${modelFamily || 'default'}, cost-ms, ${timeCost}, temperature, ${temperature ?? ''}`,
      );
    } else {
      // Non-streaming with retry logic
      const retryCount = normalizeRetryCount(modelConfig.retryCount);
      const retryInterval = modelConfig.retryInterval ?? 2000;
      const maxAttempts = retryCount + 1; // retryCount=1 means 2 total attempts (1 initial + 1 retry)

      let lastError: Error | undefined;
      const attemptErrors: Array<{ attempt: number; error: unknown }> = [];

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const { signal: attemptSignal, cleanup: cleanupAttemptSignal } =
          buildRequestAbortSignal(effectiveTimeoutMs, options?.abortSignal);
        try {
          const result = await completion.create(
            {
              model: modelName,
              messages: messagesWithImageDetail,
              ...requestConfig,
              stream: false,
            } as any,
            { signal: attemptSignal },
          );

          timeCost = Date.now() - startTime;
          requestId =
            getLatestSuccessfulResponseRequestId(openAIErrorResponseContext) ??
            result._request_id;

          debugProfileStats(
            `model, ${modelName}, mode, ${modelFamily || 'default'}, prompt-tokens, ${result.usage?.prompt_tokens || ''}, completion-tokens, ${result.usage?.completion_tokens || ''}, total-tokens, ${result.usage?.total_tokens || ''}, cost-ms, ${timeCost}, requestId, ${requestId || ''}, temperature, ${temperature ?? ''}`,
          );

          debugProfileDetail(
            `model usage detail: ${JSON.stringify(result.usage)}`,
          );

          if (!result.choices) {
            throw new Error(
              `invalid response from LLM service: ${JSON.stringify(result)}`,
            );
          }

          rawChoiceMessage = result.choices[0].message;
          const parsedMessage =
            adapter.chatCompletion.extractContentAndReasoning(
              result.choices[0].message,
            );
          content = parsedMessage.content;
          accumulatedReasoning = parsedMessage.reasoning_content;
          usage = result.usage;
          responseModelName = result.model;

          content = resolveContentWithReasoningFallback(
            content,
            accumulatedReasoning,
          );

          if (!hasUsableText(content)) {
            const errorUsage = buildUsageInfo(usage, requestId);
            if (errorUsage && modelRuntime.onUsage) {
              modelRuntime.onUsage(errorUsage);
            }
            throw new AIResponseParseError(
              'empty content from AI model',
              content || '',
              errorUsage,
              rawChoiceMessage,
            );
          }

          break; // Success, exit retry loop
        } catch (error) {
          lastError = restoreHardTimeoutError(toError(error), attemptSignal);
          attemptErrors.push({ attempt, error: lastError });
          const wasHardTimeout = isHardTimeoutError(lastError);
          if (wasHardTimeout) {
            warnCall(
              `AI call hit hard timeout (${effectiveTimeoutMs}ms, attempt ${attempt}/${maxAttempts}, model ${modelName}, slot ${modelConfig.slot})`,
            );
          }
          // Do not retry if the request was aborted by the caller
          if (options?.abortSignal?.aborted) {
            break;
          }
          if (attempt < maxAttempts) {
            warnCall(
              `AI call failed (attempt ${attempt}/${maxAttempts}), retrying in ${retryInterval}ms... Error: ${lastError.message}`,
            );
            await new Promise((resolve) => setTimeout(resolve, retryInterval));
          }
        } finally {
          cleanupAttemptSignal();
        }
      }

      if (!content) {
        assert(
          lastError,
          'AI model request failed without recording an attempt error',
        );
        throw appendAIRequestFailureSummary(
          lastError,
          attemptErrors,
          maxAttempts,
        );
      }
    }

    debugCall(`response reasoning content: ${accumulatedReasoning}`);
    debugCall(`response content: ${content}`);

    // Ensure we always have usage info for streaming responses
    if (isStreaming && !usage) {
      // Estimate token counts based on content length (rough approximation)
      const estimatedTokens = Math.max(
        1,
        Math.floor((content || '').length / 4),
      );
      usage = {
        prompt_tokens: estimatedTokens,
        completion_tokens: estimatedTokens,
        total_tokens: estimatedTokens * 2,
      } as OpenAI.CompletionUsage;
    }

    const finalUsage = buildUsageInfo(usage, requestId);
    // Report usage to the runtime-level collector if not already reported
    // (e.g. from the streaming final-chunk handler).
    if (!usageReported && finalUsage && modelRuntime.onUsage) {
      modelRuntime.onUsage(finalUsage);
    }

    const response = {
      content: content || '',
      reasoning_content: accumulatedReasoning || undefined,
      rawChoiceMessage,
      usage: finalUsage,
      isStreamed: !!isStreaming,
    };
    recordEvent?.({
      type: 'response',
      attempt: getLatestResponseAttempt(openAIErrorResponseContext),
      http: openAIErrorResponseContext.httpResponses?.at(-1),
      final: {
        content: response.content,
        reasoningContent: response.reasoning_content,
        usage: response.usage,
        requestId,
        timeCost,
        responseModelName,
      },
    });
    return response;
  } catch (e: any) {
    warnCall('call AI error', e);

    if (e instanceof AIResponseParseError) {
      throw e;
    }

    const newError = new Error(
      `failed to call ${isStreaming ? 'streaming ' : ''}AI model service (${modelName}): ${e.message}${formatOpenAIAPIErrorDetails(e, openAIErrorResponseContext)}\nTrouble shooting: https://midscenejs.com/model-provider.html`,
      {
        cause: e,
      },
    );
    throw newError;
  }
}

export type AIObjectResponse<T> = {
  // TODO: `content` is a misleading name here because this is already the parsed object response. Consider renaming it to `object` or `data`.
  content: T;
  contentString: string;
  usage?: AIUsageInfo;
  reasoning_content?: string;
  rawChoiceMessage?: unknown;
};

export function parseAIObjectResponse<T>(
  response: Awaited<ReturnType<typeof callAI>>,
  modelRuntime: ModelRuntime,
  jsonParserSource: JsonParserSource = 'generic-object',
): AIObjectResponse<T> {
  const { adapter } = modelRuntime;
  assert(response, 'empty response');
  const jsonContent = adapter.jsonParser(response.content, {
    source: jsonParserSource,
  });
  assertJsonObject(jsonContent);
  return {
    content: jsonContent as T,
    contentString: response.content,
    usage: response.usage,
    reasoning_content: response.reasoning_content,
    rawChoiceMessage: response.rawChoiceMessage,
  };
}

export async function callAIWithObjectResponse<T>(
  messages: ChatCompletionMessageParam[],
  modelRuntime: ModelRuntime,
  options?: {
    abortSignal?: AbortSignal;
    jsonParserSource?: JsonParserSource;
    retryTimes?: number;
    retryInterval?: number;
  },
): Promise<AIObjectResponse<T>> {
  const { config: modelConfig } = modelRuntime;
  return callAiAndParseWithRetry({
    callAi: (retryAttempt, previousParseError) =>
      callAI(
        withSemanticRetryFeedback(messages, previousParseError),
        modelRuntime,
        {
          abortSignal: options?.abortSignal,
          expectedJsonObjectResponse: true,
          semanticRetryAttempt: retryAttempt,
        },
      ),
    parseResponse: (response) =>
      parseAIObjectResponse<T>(
        response,
        modelRuntime,
        options?.jsonParserSource,
      ),
    toParseError: (error, response) => {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return new AIResponseParseError(
        errorMessage,
        response.content,
        response.usage,
        response.rawChoiceMessage,
        response.reasoning_content,
      );
    },
    parseRetryTimes: options?.retryTimes ?? modelConfig.retryCount,
    parseRetryInterval: options?.retryInterval ?? modelConfig.retryInterval,
    abortSignal: options?.abortSignal,
  });
}

export async function callAIWithStringResponse(
  msgs: AIArgs,
  modelRuntime: ModelRuntime,
  options?: Pick<CallAIOptions, 'abortSignal' | 'requiresOriginalImageDetail'>,
): Promise<{
  content: string;
  usage?: AIUsageInfo;
  rawChoiceMessage?: unknown;
}> {
  const { content, usage, rawChoiceMessage } = await callAI(
    msgs,
    modelRuntime,
    options,
  );
  return { content, usage, rawChoiceMessage };
}
